import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';

import { cellSx, headerSx, fieldSx, ManifestEntry } from './PartsShipmentsHelpers';

export interface ManifestTablesProps {
  serialised: ManifestEntry[];
  nonSerialised: ManifestEntry[];
  onRemoveItem: (key: string) => void;
  onUpdateQty: (key: string, qty: number) => void;
}

const groupLabelSx = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  mb: 0.5,
};

export const ManifestTables: React.FC<ManifestTablesProps> = ({
  serialised,
  nonSerialised,
  onRemoveItem,
  onUpdateQty,
}) => (
  <>
    {serialised.length > 0 && (
      <Box sx={{ mb: 1 }}>
        <Typography sx={groupLabelSx}>Serialised ({serialised.length})</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Part Type</TableCell>
                <TableCell sx={headerSx}>Serial</TableCell>
                <TableCell sx={headerSx}>Condition</TableCell>
                <TableCell sx={{ ...headerSx, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {serialised.map((m) => (
                <TableRow key={m.key} sx={{ '&:hover': { bgcolor: 'v4.tableHover' } }}>
                  <TableCell sx={cellSx}>{m.partTypeName}</TableCell>
                  <TableCell sx={cellSx}>{m.serial}</TableCell>
                  <TableCell sx={cellSx}>{m.conditionName ?? '-'}</TableCell>
                  <TableCell sx={cellSx}>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveItem(m.key)}
                      aria-label="Remove item"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )}

    {nonSerialised.length > 0 && (
      <Box>
        <Typography sx={groupLabelSx}>Non-Serialised ({nonSerialised.length})</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Part Type</TableCell>
                <TableCell sx={headerSx}>Part No</TableCell>
                <TableCell sx={headerSx}>Condition</TableCell>
                <TableCell sx={{ ...headerSx, width: 90 }}>Qty</TableCell>
                <TableCell sx={{ ...headerSx, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {nonSerialised.map((m) => (
                <TableRow key={m.key} sx={{ '&:hover': { bgcolor: 'v4.tableHover' } }}>
                  <TableCell sx={cellSx}>{m.partTypeName}</TableCell>
                  <TableCell sx={cellSx}>{m.partTypeNo}</TableCell>
                  <TableCell sx={cellSx}>{m.conditionName ?? '-'}</TableCell>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InputBase
                        type="number"
                        value={m.quantity}
                        onChange={(e) => onUpdateQty(m.key, parseInt(e.target.value) || 1)}
                        sx={{ ...fieldSx, width: 50, px: 0.5 }}
                        inputProps={{
                          min: 1,
                          ...(m.maxQuantity != null && { max: m.maxQuantity }),
                          'aria-label': `Quantity for ${m.partTypeName}`,
                        }}
                      />
                      {m.maxQuantity != null && (
                        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                          of {m.maxQuantity}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveItem(m.key)}
                      aria-label="Remove item"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )}
  </>
);
